/**
 * Barres HORIZONTALES.
 *
 * Le choix de l'horizontale n'est pas esthétique : les catégories affichées ici
 * sont des routes Expo (`/(tabs)/map`) et des versions d'app (`1.0.3`). En
 * barres verticales, ces libellés doivent être inclinés et deviennent illisibles.
 */

import { useState } from 'react';
import {
  ChartFrame,
  HATCH_FILL,
  HatchDefs,
  describeCategorical,
  formatAxisValue,
  useMeasuredWidth,
} from './chartUtils';
import type { Categorical } from './chartUtils';
import { formatInt, formatPercent } from '../lib/format';

export interface BarChartProps {
  title: string;
  rows: Categorical[];
  /** Largeur réservée aux libellés, en unités de `viewBox`. */
  labelWidth?: number;
  /** Affiche la part relative à côté de la valeur. */
  showShare?: boolean;
  emptyLabel?: string;
}

const ROW_H = 30;

/** Coupe un libellé trop long : le SVG ne sait pas faire d'ellipse tout seul. */
function ellipsize(label: string, maxChars: number): string {
  return label.length <= maxChars ? label : `${label.slice(0, maxChars - 1)}…`;
}

export function BarChart(props: BarChartProps): React.ReactElement {
  const { title, rows, labelWidth = 168, showShare = false, emptyLabel } = props;
  const [hover, setHover] = useState<number | null>(null);
  const [frameRef, vbWidth] = useMeasuredWidth<HTMLElement>();

  const height = Math.max(ROW_H, rows.length * ROW_H) + 8;
  // Dans une carte étroite, la colonne de libellés ne peut pas manger toute la
  // largeur : on la borne au tiers du graphique.
  const barLeft = Math.min(labelWidth, Math.round(vbWidth * 0.38));
  const barRight = vbWidth - 62;
  const barW = barRight - barLeft;
  const max = rows.reduce((m, r) => Math.max(m, r.value), 0);
  const total = rows.reduce((acc, r) => acc + r.value, 0);

  const hovered = hover === null ? undefined : rows[hover];

  return (
    <ChartFrame
      ariaLabel={describeCategorical(title, rows)}
      empty={rows.length === 0 || max === 0}
      emptyLabel={emptyLabel}
      frameRef={frameRef}
      onPointerLeave={() => setHover(null)}
      tooltip={
        hovered ? (
          <div
            className="chart-tip"
            style={{ left: '50%', top: `${(((hover ?? 0) * ROW_H + 4) / height) * 100}%` }}
          >
            <span className="t-when">{hovered.label}</span>
            <span className="t-row">
              <i className="t-swatch" style={{ background: hovered.color }} />
              {formatInt(hovered.value)}
              {total > 0 ? ` · ${formatPercent(hovered.value / total)}` : ''}
            </span>
          </div>
        ) : undefined
      }
    >
      <svg viewBox={`0 0 ${vbWidth} ${height}`} role="img" aria-label={describeCategorical(title, rows)}>
        <HatchDefs />
        {rows.map((row, i) => {
          const y = i * ROW_H + 4;
          const w = max > 0 ? (row.value / max) * barW : 0;
          return (
            <g
              key={row.key}
              onPointerEnter={() => setHover(i)}
              style={{ cursor: 'default' }}
            >
              {/* Bande de survol invisible : cible large, sinon il faut viser la barre. */}
              <rect x={0} y={y - 4} width={vbWidth} height={ROW_H} fill="transparent" />
              <text
                className="axis-label"
                x={barLeft - 10}
                y={y + 15}
                textAnchor="end"
                style={{ fill: hover === i ? 'var(--c-ink)' : 'var(--chart-label)' }}
              >
                {ellipsize(row.label, Math.max(6, Math.floor(barLeft / 6.6)))}
              </text>
              {/* La piste : ce que la barre N'atteint PAS. En hachures et non en
                  aplat — un aplat gris se lit comme une seconde valeur. */}
              <rect x={barLeft} y={y + 4} width={barW} height={14} rx={7} fill={HATCH_FILL} />
              <rect
                x={barLeft}
                y={y + 4}
                width={barW}
                height={14}
                rx={7}
                fill="none"
                stroke="var(--c-line)"
                strokeWidth={1}
              />
              <rect
                x={barLeft}
                y={y + 4}
                width={Math.max(w, row.value > 0 ? 3 : 0)}
                height={14}
                rx={7}
                fill={row.color}
                opacity={hover === null || hover === i ? 1 : 0.55}
              />
              <text className="axis-label" x={barRight + 8} y={y + 15} style={{ fill: 'var(--c-ink2)' }}>
                {formatAxisValue(row.value)}
                {showShare && total > 0 ? ` · ${Math.round((row.value / total) * 100)} %` : ''}
              </text>
            </g>
          );
        })}
      </svg>
    </ChartFrame>
  );
}
